import type { Uppy } from '@uppy/core';
import type { UploadedFile, UppyFile, UppyFileMeta } from '../../../../../../hooks/useUppy/types';
import { SHOULD_NOT_PROPAGATE_UP } from '../../../const';

interface WaitForFilesUploadParams {
  uppy: Uppy<UppyFileMeta, Record<string, never>>;
  fileIds: string[];
}

interface UploadResult {
  failedCount: number;
  successCount: number;
  failedFileIds: string[];
}

export function getPendingFileIds(
  uppy: Uppy<UppyFileMeta, Record<string, never>>,
  referenceId: string,
): string[] {
  return Object.values(uppy.getState().files).reduce<string[]>((accumulator, file) => {
    if (!file.progress.uploadComplete && file.meta.referenceId === referenceId) {
      accumulator.push(file.id);
    }
    return accumulator;
  }, []);
}

export function waitForFilesUpload({
  uppy,
  fileIds,
}: WaitForFilesUploadParams): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    let isSettled = false;
    const fileIdsToTrack = new Set(fileIds);
    const failedFileIds = new Set<string>();
    const completedFileIds = new Set<string>();

    const cleanup = () => {
      isSettled = true;
      uppy.off('upload-success', onUploadSuccess);
      uppy.off('upload-error', onUploadError);
      uppy.off('file-removed', onFileRemoved);
    };

    const getResult = (): UploadResult => ({
      successCount: completedFileIds.size,
      failedCount: failedFileIds.size,
      failedFileIds: Array.from(failedFileIds),
    });

    const checkCompletion = () => {
      if (isSettled) return;

      if (completedFileIds.size + failedFileIds.size >= fileIdsToTrack.size) {
        cleanup();
        resolve(getResult());
      }
    };

    const onUploadSuccess = (file: UploadedFile | undefined) => {
      if (!file || isSettled) return;
      if (!fileIdsToTrack.has(file.id)) return;

      completedFileIds.add(file.id);
      checkCompletion();
    };

    const onUploadError = (file: UploadedFile | undefined) => {
      if (!file || isSettled) return;
      if (!fileIdsToTrack.has(file.id)) return;

      failedFileIds.add(file.id);
      checkCompletion();
    };

    const onFileRemoved = (file: UppyFile<UppyFileMeta, Record<string, never>> | undefined) => {
      if (!file || isSettled) return;
      if (!fileIdsToTrack.has(file.id)) return;

      fileIdsToTrack.delete(file.id);
      if (fileIdsToTrack.size === 0) {
        cleanup();
        reject(SHOULD_NOT_PROPAGATE_UP);
      } else {
        checkCompletion();
      }
    };

    uppy.on('upload-success', onUploadSuccess);
    uppy.on('upload-error', onUploadError);
    uppy.on('file-removed', onFileRemoved);
  });
}
